import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getData } from "../../apis";
import { API } from "../../apis/config";
import LoadingScreen from "../../components/LoadingScreen";
import ScrollToTopButton from "../../components/ScrollToTopButton";
import { FestaProduct } from "../../interfaces/FestaProduct";
import { ModalInfo } from "../../interfaces/ModalInfo";
import { RootState } from "../../stores";
import { set } from "../../stores/slices/festaSlice";
import {
  changeHeaderFilter,
  convertFilterIcon,
} from "../../utils/headerFilter";
import { matchesProductNameSearch } from "../../utils/koreanSearch";
import ProductModal from "./ProductModal";
import SearchProductInput from "../../components/SearchProductInput";

function SearchProduct() {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [modal, setModal] = useState<ModalInfo<FestaProduct>>({
    isOpen: false,
    selected: undefined,
  });
  const dispatch = useDispatch();
  const searchRows = useSelector(
    (state: RootState) => state.festaProduct.searchRows,
  );
  const [sortedData, setSortedData] = useState<FestaProduct[]>([]);
  const [headerFilter] = useState<{ [key: string]: number }>({});
  const productSliceData = useSelector(
    (state: RootState) => state.festaProduct.searchRows,
  );

  const Header = {
    Name: "name",
    Location: "location",
    Sleeve: "sleeve",
    DemoInfo: "demoInfo",
    Note: "note",
  } as const;

  const openModal = (row: FestaProduct) => {
    if (modal) {
      setModal({ selected: row, isOpen: true });
    }
  };

  const clickHeader = (
    key: string,
    compareFn: (a: FestaProduct, b: FestaProduct) => number,
  ) => {
    changeHeaderFilter(headerFilter, setSortedData, key, compareFn, searchRows);
  };

  const createFilterIcon = (key: string): string => {
    return convertFilterIcon(headerFilter, key);
  };

  useEffect(() => {
    const data = async () => {
      await getData<FestaProduct[]>(API.FESTA_STAFF.PRODUCTS)
        .then((res) => {
          dispatch(set(res.result.filter((value) => value.searchable)));
        })
        .finally(() => {
          setIsLoading(false);
        });
    };
    data();
  }, [dispatch]);

  useEffect(() => {
    if (sortedData.length === 0) {
      setSortedData(productSliceData);
    }
  }, [dispatch, productSliceData, sortedData.length]);

  return (
    <div>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <div className="md:mx-20">
          <ScrollToTopButton />

          <ProductModal
            modal={modal}
            setModal={(isOpen, selected) => {
              setModal({ isOpen, selected });
            }}
          />

          <div className="flex items-center mt-6">
            <span className="text-2xl font-bold">상품 검색</span>
          </div>

          <SearchProductInput
            searchKeyword={searchKeyword}
            setSearchKeyword={setSearchKeyword}
          />
          {/* <span className="text-sm">
            상품명에 밑줄이 있다면 증정품이 있는 상품입니다.
          </span>
          <br /> */}
          <span className="text-sm">
            {/* 상품을 선택하여 추가 안내 사항을 확인하세요. */}
            테이블의 각 헤더(상품명, 위치, 저장)를
            <br className="md:hidden" />
            <span className="md:inline hidden">&nbsp;</span>
            선택하면 정렬할 수 있습니다.
          </span>
          <br />
          {/* <span className="text-sm">
            상품을 선택하면 카드 슬리브 정보를 확인할 수 있습니다.
          </span> */}
          <table className="my-4 w-full bg-white">
            <thead className="bg-gray-200">
              <tr>
                <th
                  className="w-1/3 px-4 py-2 text-center text-gray-600 cursor-pointer"
                  onClick={() => {
                    clickHeader(Header.Name, (a, b) => {
                      const valA = a.name || "";
                      const valB = b.name || "";
                      return valA.localeCompare(valB, "ko");
                    });
                  }}
                >
                  상품명{createFilterIcon(Header.Name)}
                </th>
                <th
                  className="w-3/12 px-4 py-2 text-center text-gray-600 cursor-pointer"
                  onClick={() => {
                    clickHeader(Header.Location, (a, b) => {
                      const valA = a.location || "";
                      const valB = b.location || "";
                      return valA.localeCompare(valB, "ko");
                    });
                  }}
                >
                  위치{createFilterIcon(Header.Location)}
                </th>
                {/* <th
                  className="w-2/12 px-4 py-2 text-center text-gray-600 cursor-pointer"
                  onClick={() => {
                    clickHeader(Header.Note, (a, b) =>
                      a.sleeve.localeCompare(b.sleeve, "ko")
                    );
                  }}
                >
                  슬리브{createFilterIcon(Header.Sleeve)}
                </th> */}
                <th
                  className="w-2/12 px-4 py-2 text-center text-gray-600 cursor-pointer"
                  onClick={() => {
                    clickHeader(Header.DemoInfo, (a, b) => {
                      const valA = a.demoInfo || "";
                      const valB = b.demoInfo || "";
                      return valA.localeCompare(valB, "ko");
                    });
                  }}
                >
                  체험{createFilterIcon(Header.DemoInfo)}
                </th>
                <th
                  className="w-2/12 px-4 py-2 text-center text-gray-600 cursor-pointer"
                  onClick={() => {
                    clickHeader(Header.Note, (a, b) => {
                      const valA = a.note || "";
                      const valB = b.note || "";
                      return valA.localeCompare(valB, "ko");
                    });
                  }}
                >
                  비고{createFilterIcon(Header.Note)}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData
                .filter((item) =>
                  matchesProductNameSearch(item.name ?? "", searchKeyword),
                )
                .map((item, key) => (
                  <tr
                    className={`cursor-pointer hover:bg-gray-100 border-t ${
                      item.note ? "underline" : ""
                    }`}
                    key={key}
                  >
                    <td
                      className="px-4 py-2 text-sm md:text-base"
                      onClick={() => openModal(item)}
                    >
                      {item.name}
                    </td>
                    <td className="px-4 py-2" onClick={() => openModal(item)}>
                      {item.location}
                    </td>
                    {/* <td className="px-4 py-2" onClick={() => openModal(item)}>
                      {item.sleeve}
                    </td> */}
                    <td
                      className="px-4 py-2 text-sm"
                      onClick={() => openModal(item)}
                    >
                      {item.demoInfo}
                    </td>
                    <td
                      className="px-4 py-2 text-sm"
                      onClick={() => openModal(item)}
                    >
                      {item.note}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SearchProduct;
